import { type ReactNode, useCallback, useState } from 'react'
import { type EntityId, type Preference, type PreferenceGetSuccess } from '@terros/common'
import { notifications } from '@mantine/notifications'
import { useDebouncedCallback } from '@mantine/hooks'
import { type NumberInputProps, NumberInput } from '@mantine/core'
import { useUpdatePreference } from '@front/model'
import { PreferenceInput } from './PreferenceInput'
import { usePreferenceContext } from './Context'

type NumberKeys = Extract<
  Preference,
  | 'secondsBetweenPitches'
  | 'bookingPercent'
  | 'maxMinutesSetterCanDeleteEvent'
  | 'meetingDuration'
  | 'timeBetweenAppointments'
  | 'daysBetweenPitch'
    | 'daysBetweenInterview'
    | 'daysBetweenShadow'
    | 'daysBetweenAskManager'
  | 'conversationExpiration'
  | 'inactiveDaysBeforeArchive'
  | 'updateAvailabilityTask'
>

type Props = Pick<NumberInputProps, 'allowDecimal' | 'min' | 'max' | 'placeholder' | 'disabled'> & {
  preference: NumberKeys
  targetId: EntityId
  suffix: string
}

export const PreferenceNumberInput = ({ preference, targetId, disabled, ...props }: Props): ReactNode => {
  return (
    <PreferenceInput targetId={targetId} preference={preference}>
      {({ data }) => (
        <NumberInputField data={data} disabled={disabled} preference={preference} targetId={targetId} {...props} />
      )}
    </PreferenceInput>
  )
}

type NumberInputFieldProps<T extends NumberKeys> = {
  data: PreferenceGetSuccess<T>
} & Props
const NumberInputField = <T extends NumberKeys>({
  data,
  disabled,
  preference,
  targetId,
  ...props
}: NumberInputFieldProps<T>): ReactNode => {
  const { isAllowedUpdate } = usePreferenceContext()
  const savedValue = data.current ?? data.inherited
  const [inputValue, setInputValue] = useState<string | number>(savedValue)
  const { update } = useUpdatePreference(preference, targetId)
  const debouncedUpdate = useDebouncedCallback(async (value: number) => {
    await update(value)
    notifications.show({ message: 'Preference saved', color: 'green' })
  }, 1000)

  const handleChange = useCallback(
    (newValue: string | number) => {
      setInputValue(newValue)

      if (typeof newValue === 'string') {
        debouncedUpdate.cancel()
        return
      }

      debouncedUpdate(newValue)
    },
    [debouncedUpdate]
  )

  const handleBlur = useCallback(() => {
    setInputValue((value) => (typeof value === 'string' ? savedValue : value))
  }, [savedValue])

  return (
    <NumberInput
      {...props}
      disabled={disabled || !isAllowedUpdate}
      flex={1}
      value={inputValue}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  )
}
